/**
 * Modal State Management (Valtio)
 *
 * Centralized state for all modals, drawers, and overlays.
 * Uses Valtio for consistency with checkout state management.
 *
 * @example
 * ```tsx
 * // Open a modal from anywhere
 * import { modalActions } from '@/states/modalState';
 * modalActions.open('confirm', { title: 'Delete?', onConfirm: handleDelete });
 *
 * // In a component
 * import { useModal } from '@/hooks/useModal';
 * const { isOpen, data, close } = useModal('confirm');
 * ```
 *
 * NOTE: Existing modals (CartDrawer, ListingFilters) continue to work independently.
 * This system is for NEW modals and provides optional centralized control.
 */

import { lockBodyScroll, unlockBodyScroll } from '@/lib/utils/bodyScrollLock';
import { proxy } from 'valtio';

// =============================================================================
// MODAL DATA REGISTRY
// =============================================================================
// Add modal data types here as new modals are created.
// This provides type safety when opening modals with data.

/** Address payload for the checkout edit-address modal */
export interface EditAddressModalAddress {
  id: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  postalCode: string;
  country: string;
  countryArea?: string;
  phone?: string;
  isDefaultShippingAddress?: boolean;
  isDefaultBillingAddress?: boolean;
}

export type EditAddressModalData = {
  addressId: string;
  addressType: 'shipping' | 'billing';
  address: EditAddressModalAddress;
  personalTitle?: string;
  onSave?: (address: EditAddressModalAddress) => void | Promise<void>;
};

/**
 * Payload for UG's two customer-interest drawers. They are one form opened from
 * two CTAs (BEC-24128), so they take the same data.
 */
export type UgCustomerInterestModalData = {
  productSku?: string;
  productName?: string;
  /** Variant label shown under the name, e.g. "Black". */
  productVariantName?: string | null;
  /** Card packshot, shown beside the form as live does. */
  productImage?: { url: string; alt: string } | null;
};

export interface ModalDataRegistry {
  // -------------------------------------------------------------------------
  // Utility Modals
  // -------------------------------------------------------------------------
  /**
   * UG PDP price note. The copy is authored per country
   * on the Contentful `placeholder.priceInfoText` field, so it arrives as text
   * rather than being looked up here.
   */
  'ug-price-info': {
    text: string;
  };

  /**
   * UG PDP image lightbox. The frames arrive as data rather than being fetched
   * here: `resolveUgGalleryImages` has already picked the right copy of each
   * one, and the gallery that opens this is holding the resolved list.
   */
  'ug-product-lightbox': {
    images: Array<{ url: string; alt: string }>;
    /** Which frame the gallery was parked on when the viewer clicked. */
    startIndex: number;
    /** Alt-text fallback when a Saleor media item has none. */
    productName: string;
    /** Reference number, for the PDP alt pattern (docs/SEO-RULES.md §13). */
    reference?: string;
    /**
     * Reports the frame the viewer navigated to, so the gallery underneath can
     * follow along and stay on that frame once the lightbox is dismissed.
     */
    onIndexChange?: (index: number) => void;
  };

  /** Generic confirmation dialog */
  confirm: {
    title: string;
    message: string;
    onConfirm: () => void | Promise<void>;
    onCancel?: () => void;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'default' | 'danger';
  };

  /** Checkout/payment leave confirmation — warns user before navigating away */
  'confirm-leave': {
    onConfirm: () => void;
    onCancel?: () => void;
  };

  // -------------------------------------------------------------------------
  // CMS Modals
  // -------------------------------------------------------------------------
  'cms-popup': {
    popupId: string;
    /** Human-readable title for GTM popin_view tracking */
    popinTitle?: string;
    animation?: 'fade' | 'slideRight' | 'slideDown' | null;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | null;
    backgroundColor?: string | null;
  };

  // -------------------------------------------------------------------------
  // Product Modals (Tier 1 & 2)
  // -------------------------------------------------------------------------
  /** Notify me when product is back in stock */
  'notify-me': {
    sku: string;
    productName: string;
    productImage?: string;
    /** Case material (e.g., "Stainless steel") */
    caseMaterial?: string;
    /** Dial color (e.g., "Black") */
    dialColor?: string;
    /** Locale-prefixed PDP path for notify-me submission (e.g. `/us-en/watches/...`) */
    productPath?: string;
    /** Price amount, forwarded to the notify-me backend as product interest */
    price?: number;
    /** ISO currency code for `price` */
    currency?: string;
  };

  /** Share product/page */
  share: {
    url: string;
    title: string;
    description?: string;
    image?: string;
  };

  /** Contact us modal */
  contact: {
    /** Source of the modal open (e.g., 'pdp', 'plp', 'footer') */
    source?: string;
    /** Email subject line */
    subject?: string;
    /** Product data - when provided, modal shows product card */
    product?: {
      /** Product SKU / Reference */
      sku: string;
      /** Product name */
      name: string;
      /** Product image URL */
      image?: string;
      /** Case material (e.g., "Stainless steel & 18k red gold") */
      material?: string;
      /** Price amount (raw number) */
      price?: number;
      /** Currency code (e.g., 'USD') */
      currency?: string;
      /** Pre-formatted price string (e.g., 'USD 12,900') */
      formattedPrice?: string;
    };
  };

  /** Newsletter subscription */
  newsletter: {
    campaign?: string;
    source?: string;
    email?: string;
  };

  /** Download catalogue (Chronolog) */
  'download-catalogue': {
    campaign?: string;
    source?: string;
    email?: string;
  };

  // -------------------------------------------------------------------------
  // Product Feature Modals (Tier 2)
  // -------------------------------------------------------------------------
  /** Strap size guide */
  'sizes-guide': {
    productType?: 'strap' | 'bracelet';
  };

  /** Digital gifting info */
  'send-gift': {
    sku: string;
    productName?: string;
  };

  /** Engraving customization */
  engraving: {
    variantId: string;
    sku: string;
    productName: string;
    productImage?: string;
    caseBackImage?: string;
    backEngravingCoordinates?: string;
  };

  /** Finance calculator (STUB - needs calculator components) */
  /** PDP financing calculator (Cembra CH — informational; no add-to-bag CTA). */
  'finance-calculator': {
    amount: number;
    currency: string;
    locale: string;
    paymentType: 'cembra';
    cembraFinancingPaymentMax?: number;
  };

  /**
   * UK V12 loan calculator on PDP (opened from V12Sentence).
   * Only opened when `ctaType === 'ORDER'` (`isV12Available`). `onAddToCart` should
   * forward to the latest primary CTA handler (e.g. via a ref) so click-time cart
   * state is used — same callback-in-modal pattern as `confirm` / `change-strap`.
   */
  'v12-loan-calculator': {
    amount: number;
    currency: string;
    locale: string;
    ctaLabel: string;
    onAddToCart: () => void | Promise<void>;
  };

  /** Book appointment — store finder, calendar/form, and success steps */
  'book-appointment': {
    sku?: string;
    productName?: string;
    storeId?: string;
  };

  // -------------------------------------------------------------------------
  // E-commerce Drawers (Optional - existing CartDrawer works independently)
  // -------------------------------------------------------------------------
  /**
   * Shopping cart drawer
   * NOTE: CartDrawer currently uses CartProvider. This entry is for
   * optional future migration to centralized control.
   */
  cart: undefined;

  /** Product filters drawer (PLP) */
  filters: {
    facets?: Record<string, unknown>;
    activeFilters?: Record<string, unknown>;
    totalResults?: number;
  };

  /** Search overlay */
  search: undefined;

  /** Mobile my-account drawer (guest login + authenticated quick navigation) */
  'my-account-drawer': undefined;

  // -------------------------------------------------------------------------
  // Checkout Modals (Tier 5)
  // -------------------------------------------------------------------------
  /** Edit saved address during checkout */
  'edit-address': EditAddressModalData;

  /** Price/VAT info modal */
  price: {
    title?: string;
    content?: string;
  };

  /** Service perk info modal (shipping, returns, gifting, engraving) */
  'service-perk': {
    heading: string;
    /** Contentful rich text content */
    modalText?: { json: unknown };
    /** Plain text fallback if no rich text available */
    body?: string;
    /** Optional URL to learn more */
    linkUrl?: string;
  };

  /** Terms consent dialog (express checkout — Apple Pay, etc.) */
  'terms-consent': {
    /** Header branding; `apple-pay` shows the Apple Pay wordmark. */
    variant?: 'default' | 'apple-pay';
    /** Called after checkbox validation when shopper taps Accept. */
    onAccept?: () => void;
    /** Called when shopper taps Cancel or closes the modal. */
    onDecline?: () => void;
  };

  /** Add to bag success dialog (STUB - needs Header context, notification pattern) */
  'add-to-bag': {
    product?: {
      name?: string;
      sku?: string;
      image?: string;
      price?: number;
    };
    quantity?: number;
  };

  /** Login modal (STUB - needs Saleor auth, ReCaptcha) */
  login: {
    returnUrl?: string;
    reason?: string;
    onSuccess?: () => void;
  };

  /** Register watch modal (manual reference + serial entry, or QR scan) */
  'register-watch': {
    /** Called with the registered watch record (lib/myWatches Watch shape). */
    onSuccess?: (watch: unknown) => void;
  };

  // -------------------------------------------------------------------------
  // Service Modals
  // -------------------------------------------------------------------------
  /** Serial/reference number location hint on service price lookup */
  'service-reference-hint': undefined;

  /** Watch identification failed during repair/service flows */
  'watch-not-found': {
    serialNumber: string;
    onBack?: () => void;
    onOrderService?: (serialNumber: string) => void;
  };

  /** Serial-number entry before the repair request watch step */
  'identify-watch': {
    onNavigate?: (serialNumber: string) => void;
  };

  /** Registered-watch carousel for the repair request journey */
  'select-registered-watch': undefined;

  /** Service quote refusal reason (ECS refuse quote) */
  'service-quote-refusal': {
    countryCode: string;
    quoteId: string;
    repairNumber: string;
    onSuccess?: () => void;
  };

  // -------------------------------------------------------------------------
  // Locale Switcher Modals
  // -------------------------------------------------------------------------
  /** Gallet locale/country switcher modal */
  'gallet-locale-switcher': {
    currentLocale: string;
  };

  /** UG country/currency/language switcher modal */
  'ug-locale-switcher': {
    currentLocale: string;
    /**
     * How the pop-in was opened. `auto` is the CMP-gated first visit, where a
     * choice is required and dismissal is locked; anything opened from a
     * trigger (footer, header, mobile menu) stays freely dismissible.
     */
    source?: 'auto' | 'trigger';
  };

  // -------------------------------------------------------------------------
  // Breitling-Specific Modals (Tier 3 - Feature Flagged)
  // -------------------------------------------------------------------------
  /** Boutique delivery info modal (simple wrapper) */
  'boutique-delivery': {
    content?: string;
    boutiqueName?: string;
  };

  /** Boutique pickup availability info modal */
  boutiquePickup: {
    content?: string;
  };

  /** Strap chooser modal (bundle variations) */
  'change-strap': {
    productSku?: string;
    currentStrapImage?: string;
    bundleVariations: Array<{
      sku: string;
      name: string;
      image?: string;
      color?: string;
      colorCode?: string;
      material?: string;
      strapPreviewCoords?: { x1: number; x2: number; y1: number; y2: number };
    }>;
    onSelect?: (variation: {
      sku: string;
      name: string;
      image?: string;
      color?: string;
      colorCode?: string;
      material?: string;
      strapPreviewCoords?: { x1: number; x2: number; y1: number; y2: number };
    }) => void;
  };

  /** French Oney financing modal (opened from OneySentence on PDP). */
  oney: {
    price?: number;
    currency?: string;
    oney3xMin?: number;
    oney3xMax?: number;
    oney4xMin?: number;
    oney4xMax?: number;
    productName?: string;
  };

  /** Marketing partner selector (e.g., NFL team chooser) */
  'marketing-selector': {
    title: string;
    marketingPartner: string;
    items: Array<{
      name: string;
      reference: string;
      slug?: string;
      sku?: string;
      imageUrl?: string;
    }>;
    currentItemReference?: string;
    onSelect?: (item: { name: string; reference: string; slug?: string; sku?: string }) => void;
  };

  /** UG pre-order (deposit) request, opened from a collection PDP */
  'pre-order': UgCustomerInterestModalData;

  /**
   * UG availability request, opened from a couture PDP. Same form and shape as
   * the pre-order drawer. Kept under a `ug-` id so the brand-agnostic
   * `availability-request` below stays free for Breitling and Gallet, whose own
   * availability request is a different feature (store finder, stock check).
   */
  'ug-availability-request': UgCustomerInterestModalData;

  /** Gallet / Breitling catalog availability request (MyAccount watch-avail) */
  'availability-request': {
    productSku: string;
    productName: string;
    productImage?: string;
    material?: string;
    dialColor?: string;
    formattedPrice?: string;
    price?: number;
    currency?: string;
    productPath?: string;
  };

  /** Shopping bag strap edit drawer (STUB - needs checkout integration) */
  'shopping-bag-strap-drawer': {
    cartLineId?: string;
    currentStrapSku?: string;
    watchSku?: string;
    availableStraps?: unknown[];
  };

  /** Cart strap drawer - Add strap from cart (for watch) OR from PDP/PLP (single strap) */
  'cart-strap-drawer': {
    // === From Cart: select from multiple straps for a watch ===
    /** The watch's checkout line ID (used for relatedProductWatchId metadata) */
    watchLineId?: string;
    /** Watch SKU for display */
    watchSku?: string;
    /** Watch product name */
    watchName?: string;
    /** Compatible strap slugs from watch's wmf-compatible-straps attribute */
    compatibleStrapSlugs?: string[];

    // === From PDP/PLP: single pre-selected strap ===
    /** Pre-selected strap variant ID (skips strap list, goes to details) */
    selectedStrapVariantId?: string;
    /** Pre-selected strap SKU */
    selectedStrapSku?: string;
    /** Pre-selected strap name */
    selectedStrapName?: string;
    /** Pre-selected strap image */
    selectedStrapImage?: string;
    /** Pre-selected strap price */
    selectedStrapPrice?: number;
    /** Pre-selected strap currency */
    selectedStrapCurrency?: string;
    /** Pre-selected strap product slug (for fetching buckles/tools) */
    selectedStrapSlug?: string;
    /** Whether the pre-selected strap has integrated buckle */
    hasIntegratedBuckle?: boolean;
    /** Size variant SKUs for fetching sizes in drawer (from PDP page, including current) */
    otherSizeSKUs?: string[];
    /** Compatible buckle SKUs for fetching buckles in drawer (from PDP page) */
    compatibleBuckleSKUs?: string[];

    // === Common fields ===
    /** Order CTA type from OrderApp */
    orderCtaType: 'ORDER' | 'PRE_ORDER';
    /** Channel slug for pricing */
    channel: string;
  };
}

export type ModalId = keyof ModalDataRegistry;

// =============================================================================
// MODAL STATE
// =============================================================================

interface ModalEntry<K extends ModalId = ModalId> {
  isOpen: boolean;
  data?: ModalDataRegistry[K];
}

interface ModalState {
  /**
   * Stack of open modal IDs (for nested modals).
   * The last item is the topmost modal.
   */
  stack: ModalId[];

  /**
   * Individual modal states keyed by modal ID.
   */
  modals: Partial<Record<ModalId, ModalEntry>>;

  /**
   * Scroll lock counter - incremented when something locks scroll,
   * decremented when unlocked. Scroll is only restored when counter reaches 0.
   */
  scrollLockCount: number;
}

/**
 * Main modal state (Valtio proxy)
 */
export const modalState = proxy<ModalState>({
  stack: [],
  modals: {},
  scrollLockCount: 0,
});

// =============================================================================
// MODAL ACTIONS
// =============================================================================

export const modalActions = {
  /**
   * Open a modal with optional data.
   *
   * @param id - Modal identifier from ModalDataRegistry
   * @param data - Data to pass to the modal (type-safe based on id)
   *
   * @example
   * ```tsx
   * modalActions.open('confirm', {
   *   title: 'Delete Item?',
   *   message: 'This cannot be undone.',
   *   onConfirm: () => deleteItem(id),
   * });
   * ```
   */
  open: <K extends ModalId>(id: K, data?: ModalDataRegistry[K]) => {
    const alreadyOpen = modalState.modals[id]?.isOpen === true;
    modalState.modals[id] = { isOpen: true, data };
    if (!modalState.stack.includes(id)) {
      modalState.stack.push(id);
    }

    if (!alreadyOpen) {
      modalState.scrollLockCount++;
      lockBodyScroll();
    }
  },

  /**
   * Close a specific modal or the topmost one.
   *
   * @param id - Optional modal ID. If not provided, closes the topmost modal.
   *
   * @example
   * ```tsx
   * modalActions.close('confirm'); // Close specific modal
   * modalActions.close();          // Close topmost modal
   * ```
   */
  close: (id?: ModalId) => {
    const target = id || modalState.stack[modalState.stack.length - 1];
    if (target && modalState.modals[target]) {
      modalState.modals[target] = { isOpen: false };
      modalState.stack = modalState.stack.filter((m) => m !== target);

      // Decrement scroll lock counter
      modalState.scrollLockCount = Math.max(0, modalState.scrollLockCount - 1);
      unlockBodyScroll();
    }
  },

  /**
   * Close all open modals.
   */
  closeAll: () => {
    const locksToRelease = modalState.stack.length;
    modalState.scrollLockCount = Math.max(0, modalState.scrollLockCount - locksToRelease);

    modalState.stack = [];
    for (const id of Object.keys(modalState.modals) as ModalId[]) {
      if (modalState.modals[id]) {
        modalState.modals[id] = { isOpen: false };
      }
    }

    for (let i = 0; i < locksToRelease; i++) {
      unlockBodyScroll();
    }
  },

  /**
   * Check if any modal is currently open.
   */
  hasOpenModal: () => modalState.stack.length > 0,

  /**
   * Get the topmost open modal ID.
   */
  getTopModal: () => modalState.stack[modalState.stack.length - 1] || null,

  /**
   * Lock body scroll (for non-modal components like CartDrawer).
   * Call unlockScroll when done.
   */
  lockScroll: () => {
    modalState.scrollLockCount++;
    lockBodyScroll();
  },

  /**
   * Unlock body scroll (for non-modal components like CartDrawer).
   * Only restores scroll when all locks are released.
   */
  unlockScroll: () => {
    modalState.scrollLockCount = Math.max(0, modalState.scrollLockCount - 1);
    unlockBodyScroll();
  },
};
